import React from "react";
import { X, Loader2, Flag } from "lucide-react";

export default function InventoryShipModal({
  isOpen,
  onClose,
  shipPo,
  loadingShipment,
  isEditingAddress,
  setIsEditingAddress,
  editAddressData,
  setEditAddressData,
  savingAddress,
  handleSaveAddress,
  shipChecklist,
  toggleShipArrived,
  shipShowPartialInput,
  toggleShipPartialInput,
  handleShipItemQtyChange,
  toggleShipFlagged,
  shipShowFlagInput,
  handleShipItemFlagReasonChange,
  handleShipItemNotesChange,
  shipGeneralNotes,
  setShipGeneralNotes,
  actualShippingCost,
  setActualShippingCost,
  onSubmit,
  isPartialShipment,
  shipmentToUpdate,
  carriers = []
}) {
  if (!isOpen || !shipPo) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content" style={{ maxWidth: "680px", maxHeight: "88vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <div className="modal-header" style={{ flexShrink: 0 }}>
          <h3>{isPartialShipment ? "Ship Partial PO Items to Client" : "Ship PO Items to Client"}</h3>
          <button className="modal-close-btn" onClick={onClose}>
            <X size={18} />
          </button>
        </div>
        
        {loadingShipment ? (
          <div className="loading-container" style={{ padding: "3rem" }}>
            <Loader2 className="spinner-icon" size={24} />
            <span>Checking shipment records...</span>
          </div>
        ) : (
          <form onSubmit={onSubmit} style={{ display: "flex", flexDirection: "column", flex: 1, overflow: "hidden", minHeight: 0 }}>
            <div style={{ padding: "1.5rem", overflowY: "auto", flex: 1, minHeight: 0 }}>
              
              {/* Destination Address Confirmation */}
              {!isEditingAddress ? (
                <div style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.02)", 
                  border: "1px solid var(--border-color)", 
                  padding: "1rem", 
                  borderRadius: "6px", 
                  marginBottom: "1.5rem",
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-start",
                  gap: "1rem"
                }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ margin: "0 0 0.5rem 0", color: "var(--primary)", fontSize: "0.95rem" }}>Confirm Client Delivery Address</h4>
                    <p style={{ margin: 0, fontSize: "0.85rem", color: "var(--text-primary)", lineHeight: 1.4 }}>
                      <strong>{shipPo.clientName}</strong><br />
                      {shipPo.shipToAddress}
                      {shipPo.shipToAddress2 ? `, ${shipPo.shipToAddress2}` : ""}<br />
                      {shipPo.shipToCity}, {shipPo.shipToState} {shipPo.shipToZip}
                    </p>
                    <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                      Carrier: <strong style={{ color: "var(--text-primary)" }}>{shipmentToUpdate?.carrier || "N/A"}</strong> | Tracking: <strong style={{ color: "var(--text-primary)" }}>{shipmentToUpdate?.trackingNumber || "N/A"}</strong>
                    </p>
                  </div>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => {
                      setEditAddressData({
                        shipToAddress: shipPo.shipToAddress || "",
                        shipToAddress2: shipPo.shipToAddress2 || "",
                        shipToCity: shipPo.shipToCity || "",
                        shipToState: shipPo.shipToState || "",
                        shipToZip: shipPo.shipToZip || "",
                        carrier: shipmentToUpdate?.carrier || "",
                        trackingNumber: shipmentToUpdate?.trackingNumber || ""
                      });
                      setIsEditingAddress(true);
                    }}
                    style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", minHeight: "auto" }}
                  >
                    Update
                  </button>
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: "rgba(255, 255, 255, 0.02)", 
                  border: "1px solid var(--border-color)", 
                  padding: "1rem", 
                  borderRadius: "6px", 
                  marginBottom: "1.5rem" 
                }}>
                  <h4 style={{ margin: "0 0 0.75rem 0", color: "var(--primary)", fontSize: "0.95rem" }}>Update Client Delivery Address</h4>
                  
                  <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Street Address</label>
                    <input
                      type="text"
                      className="form-input"
                      style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                      value={editAddressData.shipToAddress}
                      onChange={(e) => setEditAddressData({ ...editAddressData, shipToAddress: e.target.value })}
                      placeholder="e.g. 123 Industrial Way"
                    />
                  </div>

                  <div className="form-group" style={{ marginBottom: "0.75rem" }}>
                    <label className="form-label" style={{ fontSize: "0.75rem" }}>Suite / Apt / Gate (Optional)</label>
                    <input
                      type="text"
                      className="form-input"
                      style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                      value={editAddressData.shipToAddress2}
                      onChange={(e) => setEditAddressData({ ...editAddressData, shipToAddress2: e.target.value })}
                      placeholder="e.g. Suite 400"
                    />
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>City</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                        value={editAddressData.shipToCity}
                        onChange={(e) => setEditAddressData({ ...editAddressData, shipToCity: e.target.value })}
                        placeholder="Chicago"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>State</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                        value={editAddressData.shipToState}
                        onChange={(e) => setEditAddressData({ ...editAddressData, shipToState: e.target.value })}
                        placeholder="IL"
                      />
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>ZIP Code</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                        value={editAddressData.shipToZip}
                        onChange={(e) => setEditAddressData({ ...editAddressData, shipToZip: e.target.value })}
                        placeholder="60609"
                      />
                    </div>
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "0.5rem", marginBottom: "1rem" }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Carrier</label>
                      <select
                        className="form-input"
                        style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                        value={editAddressData.carrier || ""}
                        onChange={(e) => setEditAddressData({ ...editAddressData, carrier: e.target.value })}
                      >
                        <option value="">-- Select Carrier --</option>
                        {carriers.map((c) => (
                          <option key={c} value={c}>
                            {c}
                          </option>
                        ))}
                        {editAddressData.carrier && !carriers.includes(editAddressData.carrier) && (
                          <option value={editAddressData.carrier}>
                            {editAddressData.carrier}
                          </option>
                        )}
                      </select>
                    </div>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontSize: "0.75rem" }}>Tracking Number</label>
                      <input
                        type="text"
                        className="form-input"
                        style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                        value={editAddressData.trackingNumber || ""}
                        onChange={(e) => setEditAddressData({ ...editAddressData, trackingNumber: e.target.value })}
                        placeholder="e.g. TRK12345678"
                      />
                    </div>
                  </div>

                  <div style={{ display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => setIsEditingAddress(false)}
                      style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", minHeight: "auto" }}
                      disabled={savingAddress}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleSaveAddress}
                      style={{ padding: "0.3rem 0.6rem", fontSize: "0.75rem", minHeight: "auto" }}
                      disabled={savingAddress}
                    >
                      {savingAddress ? "Saving..." : "Save Address"}
                    </button>
                  </div>
                </div>
              )}
              
              {/* Checklist Table */}
              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label className="form-label" style={{ marginBottom: "0.75rem" }}>Item Dispatch Verification Checklist</label>
                <div style={{ 
                  maxHeight: "300px", 
                  overflowY: "auto", 
                  border: "1px solid var(--border-color)", 
                  borderRadius: "6px",
                  backgroundColor: "rgba(0, 0, 0, 0.15)"
                }}>
                  {shipPo.items && shipPo.items.length === 0 ? (
                    <div style={{ padding: "1rem", textAlign: "center", color: "var(--text-secondary)" }}>
                      No items in this PO
                    </div>
                  ) : (
                    shipPo.items.map(item => {
                      const check = shipChecklist[item.id] || { arrived: true, receivedQuantity: item.quantity, isFlagged: false, flagReason: "", notes: "" };
                      return (
                        <div 
                          key={item.id} 
                          style={{
                            padding: "1rem",
                            borderBottom: "1px solid var(--border-color)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "0.75rem",
                            backgroundColor: check.arrived ? "transparent" : "rgba(239, 68, 68, 0.02)"
                          }}
                        >
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "1rem" }}>
                            <div style={{ flex: 1 }}>
                              <span style={{ fontWeight: 600, color: "var(--text-primary)", display: "block", fontSize: "0.95rem" }}>
                                {item.description}
                              </span>
                              <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)" }}>
                                Ordered: <strong style={{ color: "var(--primary)" }}>{item.quantity} units</strong> {item.vendor ? `— Vendor: ${item.vendor}` : ""}
                              </span>
                            </div>
                            <div style={{ display: "flex", alignItems: "center", gap: "0.75rem" }}>
                              <span style={{ fontSize: "0.8rem", fontWeight: 600, color: check.arrived ? "var(--success)" : "var(--text-muted)" }}>
                                {check.arrived ? (check.alreadyShipped ? "Shipped (Previous)" : "Shipped") : "Not Shipped"}
                              </span>
                              <label className="switch" style={{ opacity: check.alreadyShipped ? 0.6 : 1 }}>
                                <input 
                                  type="checkbox" 
                                  checked={check.arrived}
                                  onChange={() => toggleShipArrived(item.id)}
                                  disabled={check.alreadyShipped}
                                />
                                <span className="slider"></span>
                              </label>
                            </div>
                          </div>
                          
                          {check.arrived && (
                            <div style={{ 
                              display: "flex", 
                              flexDirection: "column", 
                              gap: "0.5rem", 
                              borderTop: "1px dashed var(--border-color)", 
                              paddingTop: "0.5rem",
                              marginTop: "0.25rem" 
                            }}>
                              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", flexWrap: "wrap" }}>
                                <button
                                  type="button"
                                  className={`btn ${shipShowPartialInput[item.id] ? "btn-primary" : "btn-secondary"}`}
                                  style={{ padding: "0.2rem 0.5rem", fontSize: "0.75rem", minHeight: "auto" }}
                                  onClick={() => toggleShipPartialInput(item.id)}
                                  disabled={check.alreadyShipped}
                                >
                                  {shipShowPartialInput[item.id] ? "Full Qty" : "Partial"}
                                </button>
                                
                                <button
                                  type="button"
                                  className={`btn ${check.isFlagged ? "btn-danger" : "btn-secondary"}`}
                                  style={{ 
                                    padding: "0.2rem 0.5rem", 
                                    fontSize: "0.75rem", 
                                    minHeight: "auto", 
                                    display: "flex", 
                                    alignItems: "center", 
                                    gap: "0.25rem" 
                                  }}
                                  onClick={() => toggleShipFlagged(item.id)}
                                  disabled={check.alreadyShipped}
                                >
                                  <Flag size={12} fill={check.isFlagged ? "currentColor" : "none"} />
                                  {check.isFlagged ? "Flagged" : "Flag Issue"}
                                </button>
                                
                                <span style={{ fontSize: "0.8rem", color: "var(--text-secondary)", marginLeft: "auto" }}>
                                  Shipped: <strong style={{ color: "var(--text-primary)" }}>{check.receivedQuantity}</strong> / {item.quantity}
                                </span>
                              </div>
                              
                              {shipShowPartialInput[item.id] && (
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label className="form-label" style={{ fontSize: "0.75rem" }}>Specify Shipped Quantity</label>
                                  <input
                                    type="number"
                                    className="form-input"
                                    style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto", maxWidth: "150px" }}
                                    min="0"
                                    max={item.quantity}
                                    value={check.receivedQuantity}
                                    onChange={(e) => handleShipItemQtyChange(item.id, e.target.value)}
                                    disabled={check.alreadyShipped}
                                  />
                                </div>
                              )}
                              
                              {shipShowFlagInput[item.id] && (
                                <div className="form-group" style={{ margin: 0 }}>
                                  <label className="form-label" style={{ fontSize: "0.75rem", color: "var(--danger)" }}>What is wrong with the line item?</label>
                                  <input
                                    type="text"
                                    className="form-input"
                                    style={{ padding: "0.4rem", fontSize: "0.85rem", height: "auto" }}
                                    placeholder="e.g. item damaged, missing pieces"
                                    value={check.flagReason}
                                    onChange={(e) => handleShipItemFlagReasonChange(item.id, e.target.value)}
                                    required
                                    disabled={check.alreadyShipped}
                                  />
                                </div>
                              )}
                              
                              <div className="form-group" style={{ margin: 0 }}>
                                <input
                                  type="text"
                                  className="form-input"
                                  style={{ padding: "0.4rem", fontSize: "0.8rem", height: "auto", borderStyle: "dotted", backgroundColor: "transparent" }}
                                  placeholder="Add comments / note for this item..."
                                  value={check.notes}
                                  onChange={(e) => handleShipItemNotesChange(item.id, e.target.value)}
                                  disabled={check.alreadyShipped}
                                />
                              </div>
                            </div>
                          )}
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
              
              {/* Actual Shipping Cost for this Shipment */}
              <div className="form-group" style={{ marginBottom: "1.25rem" }}>
                <label className="form-label" htmlFor="actualShippingCost">
                  Actual Shipping Cost ($)
                </label>
                <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
                  <span style={{ position: "absolute", left: "0.75rem", color: "var(--text-muted)", fontWeight: 600 }}>$</span>
                  <input
                    id="actualShippingCost"
                    type="number"
                    step="0.01"
                    min="0"
                    className="form-input"
                    style={{ paddingLeft: "1.75rem" }}
                    placeholder="0.00"
                    value={actualShippingCost || ""}
                    onChange={(e) => setActualShippingCost && setActualShippingCost(e.target.value)}
                  />
                </div>
                <span style={{ fontSize: "0.75rem", color: "var(--text-secondary)", marginTop: "0.3rem", display: "block" }}>
                  Total actual carrier shipping amount for all items in this shipment.
                </span>
              </div>

              {/* General Shipping Notes */}
              <div className="form-group" style={{ marginBottom: "1.5rem" }}>
                <label className="form-label" htmlFor="shipGeneralNotes">General Shipping Notes</label>
                <textarea
                  id="shipGeneralNotes"
                  className="form-input"
                  style={{ minHeight: "60px", padding: "0.5rem" }}
                  placeholder="Enter any general notes about the shipping (e.g. tracking links, courier)..."
                  value={shipGeneralNotes}
                  onChange={(e) => setShipGeneralNotes(e.target.value)}
                />
              </div>
            </div>
            
            {/* Pinned Modal Actions */}
            <div style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: "0.75rem",
              padding: "1rem 1.5rem",
              borderTop: "1px solid var(--border-color)",
              backgroundColor: "var(--bg-card)",
              flexShrink: 0,
              zIndex: 10
            }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary">
                Confirm Shipment Dispatch
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
